import _ from 'underscore';
import { useCallback, useContext, useMemo, useState } from 'react';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useCustomerLevelVariables, usePostponeReadVariables, usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { useCountries, useCurrencyCountriesMappings } from '@bingads-webui/overreact-cc';
import { usePromisify } from '@bingads-webui-react/overreact';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

export const SupportedCurrency = ['ARS', 'AUD', 'BRL', 'CAD', 'CHF', 'CLP', 'COP', 'DKK', 'GBP', 'HKD', 'IDR', 'INR', 'JPY', 'MXN', 'MYR', 'NGN', 'NOK', 'NZD', 'PEN', 'PHP', 'PLN', 'SAR', 'SEK', 'SGD', 'THB', 'TRY', 'ZAR', 'EUR', 'USD'];
const defaultSupportedCountryList = [{
  Id: 191,
  Code: 'US',
  Name: 'United States',
  Currency: 'USD',
  IsVatApplicable: false,
  CountryCallingCode: 1,
  IsPaperlessBillingAllowed: true,
  PaperlessBillingEnforced: true,
}];

export function useGetSupportedCountryList() {
  const serverContext = useContext(ServerContext);
  const [supportedCountryList, setSupportedCountryList] = useState([]);
  const customerLevelVariables = useCustomerLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();

  const [, { read: readCountriesFn }] = useCountries(customerLevelVariables, postponeReadConfig);
  const [readCountriesAsync] = usePromisify([readCountriesFn]);
  const readCountries = useCallback(
    (args = {}, ...rest) => readCountriesAsync(args, ...rest),
    [readCountriesAsync]
  );

  const [, { read: readCurrencyCountriesMappingsFn }]
    = useCurrencyCountriesMappings(customerLevelVariables, postponeReadConfig);
  const [readCurrencyCountriesMappingsAsync] = usePromisify([readCurrencyCountriesMappingsFn]);
  const readCurrencyCountriesMappings = useCallback(
    (args = {}, ...rest) => readCurrencyCountriesMappingsAsync(args, ...rest),
    [readCurrencyCountriesMappingsAsync]
  );

  const instrumentation = useContext(InstrumentationContext) as any;
  const readCountryActivity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.readSupportedCountryList.create();
    }
    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const isGoogleAuthIntlNewSignInPageEnabled = usePilotsValue('GoogleAuthIntlNewSignInPage');

  const getSupportedCountryList = useCallback(
    ({ countries: prefetchedCountries, currencyCountriesMappings: prefetchedMappings } = {}) =>
      Promise.all([
        prefetchedCountries ? Promise.resolve(prefetchedCountries) : readCountries(),
        prefetchedMappings ? Promise.resolve(prefetchedMappings) : readCurrencyCountriesMappings(),
      ]).then(([countries, currencyCountriesMappings]) => {
        if (_.isEmpty(countries) || _.isEmpty(currencyCountriesMappings)) {
          return defaultSupportedCountryList;
        }

        const allCountries = _.map(countries, c => _.get(c, 'value'));

        const allSupportedCurrencyObj =
            _.compact(_.map(SupportedCurrency, c => _.findWhere(currencyCountriesMappings, { currency: c })));
        let supportedCountryCodeList = _.uniq(_.flatten(_.map(allSupportedCurrencyObj, o => o.countries)));

        if (['Google', 'Facebook'].includes((serverContext as any).AuthType)) {
          if (isGoogleAuthIntlNewSignInPageEnabled) {
            supportedCountryCodeList = _.filter(supportedCountryCodeList, code => !['CN', 'IN'].includes(code));
          } else {
            supportedCountryCodeList = ['US'];
          }
        }

        const supportedCountries = _.sortBy(_.filter(allCountries, c => supportedCountryCodeList.includes(c.Code)), 'Code');

        setSupportedCountryList(supportedCountries);

        return supportedCountries;
      })
        .catch((error) => {
          readCountryActivity.trace(`Error in readSupportedCountryList: ${JSON.stringify(error)}`);
          return defaultSupportedCountryList;
        }),
    [readCountries, readCountryActivity, readCurrencyCountriesMappings,
      (serverContext as any).AuthType, isGoogleAuthIntlNewSignInPageEnabled]
  );

  return { supportedCountryList, getSupportedCountryList };
}
